import { useEffect, useMemo } from "react";

import { cloneDeep } from "lodash";
import {
  BooleanParam,
  createEnumParam,
  useQueryParam,
  withDefault,
} from "use-query-params";

import { MetricsExplorerProvider } from "@features/metrics-explorer";
import {
  type MetricsExplorerChartState,
  type MetricsExplorerFilters,
  type TileDescriptor,
} from "@features/metrics-explorer/types";
import { useUserAttributionSettings } from "@hooks/use-user-attribution-settings";
import {
  CustomJsonCompressedParam,
  CustomJsonParam,
  type DateRangeChoiceV4,
  type FixedDateInterval,
  type ITimeGranularityV2,
  TimeGranularityV2,
} from "@north-beam/nb-common";

import { MetricsExplorerContent } from "./metrics-explorer-content";

const DateRangeQueryParam = CustomJsonParam<
  FixedDateInterval | DateRangeChoiceV4
>();

const TimeGranularityParam = createEnumParam<ITimeGranularityV2>(
  TimeGranularityV2.keyOrder,
);

const TilesParam = withDefault(
  CustomJsonCompressedParam<TileDescriptor[]>(),
  [],
);

interface Props {
  tiles: TileDescriptor[];
  filters: Omit<MetricsExplorerFilters, "dateRange" | "dateRangeInterval"> & {
    dateRange: DateRangeChoiceV4 | FixedDateInterval;
  };
  chartState: MetricsExplorerChartState;
  onClose: () => void;
}

export const SalesMetricsExplorerProvider = ({
  tiles: initialTiles,
  filters: initialFilters,
  chartState: initialChartState,
  onClose,
}: Props) => {
  const { attributionModel, attributionWindow, accountingMode } =
    useUserAttributionSettings();

  const [dateRange, setDateRange] = useQueryParam(
    "meDateRange",
    withDefault<
      FixedDateInterval | DateRangeChoiceV4,
      FixedDateInterval | DateRangeChoiceV4
    >(DateRangeQueryParam, "last7Days"),
    { updateType: "pushIn" },
  );

  const [timeGranularity, setTimeGranularity] = useQueryParam(
    "meTimeGranularity",
    withDefault(TimeGranularityParam, "daily" as const),
    { updateType: "pushIn" },
  );

  const [alignAxesScale, setAlignAxesScale] = useQueryParam(
    "meAlignAxesScale",
    withDefault(BooleanParam, false),
    { updateType: "pushIn" },
  );

  const [tiles, setTiles] = useQueryParam("meTiles", TilesParam, {
    updateType: "pushIn",
    enableBatching: false,
  });

  useEffect(() => {
    setDateRange(initialFilters.dateRange);
    setTimeGranularity(initialFilters.timeGranularity);
    setAlignAxesScale(initialChartState.alignAxesScale);
    setTiles(
      cloneDeep(
        initialTiles.map((tile) => ({
          ...tile,
          groupId: undefined,
          groupName: undefined,
          isStacked: undefined,
        })),
      ),
    );
  }, [
    initialFilters.dateRange,
    initialFilters.timeGranularity,
    initialChartState.alignAxesScale,
    initialTiles,
  ]);

  const filters = useMemo(() => {
    return {
      accountingMode,
      attributionModel,
      attributionWindow,
      dateRange,
      timeGranularity,
      filterQuery: null,
      advancedSearch: null,
      level: initialFilters.level,
    };
  }, [
    accountingMode,
    attributionModel,
    attributionWindow,
    dateRange,
    timeGranularity,
    initialFilters.level,
  ]);

  return (
    <MetricsExplorerProvider
      filters={filters}
      chartState={{ alignAxesScale }}
      tiles={tiles}
      setAlignAxesScale={(val) => setAlignAxesScale(val)}
      setTiles={(val) => setTiles(val)}
    >
      <MetricsExplorerContent
        dateRange={dateRange}
        timeGranularity={timeGranularity}
        onDateRangeChange={(val) => setDateRange(val)}
        onTimeGranularityChange={(val) => setTimeGranularity(val)}
        onClose={onClose}
      />
    </MetricsExplorerProvider>
  );
};
